import useI18n from '@/shared/hooks/usei18n'
import {
  NumericInputGroup,
  CheckboxGroup,
  Button,
  ListboxGroup,
  ElectricityProductionMethod,
  PageSection
} from '@/common'
import gridEmissionFactorByCountry from '@/common/config/gridEmissionFactorByCountry'
import { useCallback, useMemo } from 'react'
import { SupplierEmissionsReportFormData } from './SupplierEmissionsReportForm.schema'
import { getSpecificIndirectEmbeddedEmissions } from '@/shared/utils/getSpecificIndirectEmbeddedEmissions'
import { getElectricityConsumption } from '@/shared/utils/getElectricityConsumption'

export function SupplierEmissionsReportEmissionsDataForm({
  data,
  onChange,
  supplierCountry
}: {
  data: SupplierEmissionsReportFormData
  onChange: (data: SupplierEmissionsReportFormData) => void
  supplierCountry: string
}) {
  const { t } = useI18n()

  const defaultElectricityEmissionFactor =
    gridEmissionFactorByCountry[supplierCountry as keyof typeof gridEmissionFactorByCountry] || 0

  const onElectricityConsumptionChange = useCallback(
    (value: string) => {
      if (!data.provideSIEEDirectly) {
        onChange({
          ...data,
          electricityConsumptionMWh: value,
          specificIndirectEmbeddedEmissions: getSpecificIndirectEmbeddedEmissions(
            Number(value),
            Number(data.electricityEmissionFactor)
          ).toString()
        })
      }
    },
    [onChange, data]
  )

  const onElectricityEmissionFactorChange = useCallback(
    (value: string) => {
      if (!data.provideSIEEDirectly) {
        onChange({
          ...data,
          electricityEmissionFactor: value,
          specificIndirectEmbeddedEmissions: getSpecificIndirectEmbeddedEmissions(
            Number(data.electricityConsumptionMWh),
            Number(value)
          ).toString()
        })
      }
    },
    [onChange, data]
  )

  const onSpecificIndirectEmbeddedEmissionsChange = useCallback(
    (value: string) => {
      if (data.provideSIEEDirectly) {
        onChange({
          ...data,
          specificIndirectEmbeddedEmissions: value,
          electricityConsumptionMWh: getElectricityConsumption(
            Number(value),
            Number(defaultElectricityEmissionFactor)
          ).toString()
        })
      }
    },
    [onChange, data, defaultElectricityEmissionFactor]
  )

  const onProvideSIEEDirectlyChange = useCallback(
    (checked: boolean) => {
      onChange({
        ...data,
        provideSIEEDirectly: checked,
        electricityProductionMethod: ElectricityProductionMethod.GRID,
        electricityEmissionFactor: defaultElectricityEmissionFactor.toString(),
        electricityConsumptionMWh: '',
        specificIndirectEmbeddedEmissions: ''
      })
    },
    [data, onChange, defaultElectricityEmissionFactor]
  )

  const onResetDefaultValues = useCallback(() => {
    onChange({
      ...data,
      electricityEmissionFactor: defaultElectricityEmissionFactor.toString(),
      specificIndirectEmbeddedEmissions: getSpecificIndirectEmbeddedEmissions(
        Number(data.electricityConsumptionMWh),
        Number(defaultElectricityEmissionFactor)
      ).toString()
    })
  }, [data, onChange, defaultElectricityEmissionFactor])

  const onElectricityProductionMethodChange = useCallback(
    (value: ElectricityProductionMethod | '') => {
      if (value === ElectricityProductionMethod.GRID) {
        return onChange({
          ...data,
          electricityProductionMethod: value,
          electricityEmissionFactor: defaultElectricityEmissionFactor.toString(),
          electricityConsumptionMWh: getElectricityConsumption(
            Number(data.specificIndirectEmbeddedEmissions),
            Number(defaultElectricityEmissionFactor)
          ).toString()
        })
      }

      return onChange({
        ...data,
        electricityProductionMethod: value,
        electricityEmissionFactor: '',
        electricityConsumptionMWh: ''
      })
    },
    [onChange, data, defaultElectricityEmissionFactor]
  )

  const isResetDefaultValuesEnabled = useMemo(() => {
    return data.electricityEmissionFactor !== defaultElectricityEmissionFactor.toString()
  }, [data, defaultElectricityEmissionFactor])

  return (
    <PageSection
      title={t(
        'supplierEmissionsReport:createSupplierEmissionsReportDialog.sections.emissionsData'
      )}
      actions={
        <Button
          variant="default"
          sizeType="sm"
          disabled={!isResetDefaultValuesEnabled}
          onClick={onResetDefaultValues}
        >
          {t('supplierEmissionsReport:supplierEmissionsReportInputFields.resetDefaultValues')}
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <NumericInputGroup
            label={t(
              'supplierEmissionsReport:supplierEmissionsReportInputFields.specificDirectEmbeddedEmissions'
            )}
            value={data.specificDirectEmbeddedEmissions}
            suffix={'tCO₂/t'}
            onChange={value =>
              onChange({ ...data, specificDirectEmbeddedEmissions: value.target.value })
            }
            numericFormat={{
              decimalScale: 3,
              allowNegative: false
            }}
          />
          <div className="flex flex-col gap-2">
            <NumericInputGroup
              label={t(
                'supplierEmissionsReport:supplierEmissionsReportInputFields.specificIndirectEmbeddedEmissions'
              )}
              value={data.specificIndirectEmbeddedEmissions}
              disabled={!data.provideSIEEDirectly}
              suffix={'tCO₂/t'}
              numericFormat={{
                decimalScale: 3,
                allowNegative: false
              }}
              onChange={value => onSpecificIndirectEmbeddedEmissionsChange(value.target.value)}
            />
            <CheckboxGroup
              label={t(
                'supplierEmissionsReport:supplierEmissionsReportInputFields.provideSIEEDirectly'
              )}
              id="evidence"
              checked={data.provideSIEEDirectly}
              onChange={onProvideSIEEDirectlyChange}
            />
          </div>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <ListboxGroup<ElectricityProductionMethod>
              label="Electricity Source"
              disabled={data.provideSIEEDirectly}
              options={[
                {
                  value: ElectricityProductionMethod.GRID,
                  label: t('supplierEmissionsReport:electricitySources.GRID')
                },
                {
                  value: ElectricityProductionMethod.PPA,
                  label: t('supplierEmissionsReport:electricitySources.PPA')
                },
                {
                  value: ElectricityProductionMethod.DIRECT_TECHNICAL_LINK,
                  label: t('supplierEmissionsReport:electricitySources.DIRECT_TECHNICAL_LINK')
                }
              ]}
              value={data.electricityProductionMethod}
              onChange={onElectricityProductionMethodChange}
            />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <NumericInputGroup
            disabled={data.provideSIEEDirectly}
            label={t(
              'supplierEmissionsReport:supplierEmissionsReportInputFields.electricityConsumptionMWh'
            )}
            value={data.electricityConsumptionMWh}
            suffix={'MWh/t'}
            onChange={value => onElectricityConsumptionChange(value.target.value)}
            numericFormat={{
              decimalScale: 3,
              allowNegative: false
            }}
          />
          <NumericInputGroup
            disabled={data.provideSIEEDirectly}
            label={t(
              'supplierEmissionsReport:supplierEmissionsReportInputFields.electricityEmissionFactor'
            )}
            value={data.electricityEmissionFactor}
            suffix={'tCO₂e/MWh'}
            onChange={value => onElectricityEmissionFactorChange(value.target.value)}
            numericFormat={{
              decimalScale: 3,
              allowNegative: false
            }}
          />
        </div>
      </div>
    </PageSection>
  )
}
